import { Avatar, Divider, Grid, Body, Stack, Typography, StackItem } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { compactArray } from '@mews/fp';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { DetailedUser, ProfileDataDiscriminator, UserStatus } from '@mews-commander/data-access';
import { Loader, LocalizedSmallParagraph } from '@mews-commander/ui';
import { LocalizedText, useFeatureFlag, useLocalizeEnum } from '@mews/framework';
import { useGetAvatarSrc } from '@mews-commander/utils';
import { useLocalizePrivilege } from '../../../../roles/PrivilegesTable/utils';
import { useUserContext } from '../../../context';
import { useEnterpriseProfilesSearch } from '../../../queries';
import { UserType } from '../../../types';
import { getPrivilegesArray } from '../../../utils';
import { UserStatusTag } from '../../UserStatusTag';
import { ResendInviteButton } from '../ResendInviteButton';
import { CopyInviteButton } from '../CopyInviteButton';

export const UserDetailContentProfileData = ({ user }: { user: DetailedUser }) => {
    const localizeEnum = useLocalizeEnum();
    const employeeIncentiveCodeEnabled = useFeatureFlag(FeatureFlag.employeeIncentiveCodeEnabled);
    const { userType } = useUserContext();
    const localizePrivilege = useLocalizePrivilege();
    const activePrivileges = user.privileges ? getPrivilegesArray(user.privileges) : [];

    // only property users in MMP can have roles and permissions at the same time
    const showPermissions =
        !user.roles?.length || (userType === UserType.Properties && !user.overrideComparator?.equalPrivileges);

    return (
        <Stack vertical spacing="500">
            <Stack vertical spacing="200">
                <Typography textStyle="titleMediumStrong" as="h3">
                    <LocalizedText textKey={TextKey.Profile} />
                </Typography>
                <Stack vertical>
                    <Divider />

                    {userType === UserType.Portfolio ? null : (
                        <ProfileDataItem>
                            <LocalizedSmallParagraph variant="secondary" textKey={TextKey.Department} />
                            {renderListOrDash(user.departments, (item) => item.name)}
                        </ProfileDataItem>
                    )}

                    <ProfileDataItem>
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.LearningPath} />
                        {renderListOrDash(user.learningPaths, (item) => localizeEnum('LearningPath', item))}
                    </ProfileDataItem>

                    {userType === UserType.Properties ? null : (
                        <ProfileDataItem>
                            <LocalizedSmallParagraph variant="secondary" textKey={TextKey.Supervisor} />
                            {user.superiorProfileIds?.length ? (
                                <Stack vertical spacing="150">
                                    {user.superiorProfileIds?.map((id) => <SuperiorAvatar userId={id} key={id} />)}
                                </Stack>
                            ) : (
                                <Body size="small">{UnicodeEntity.Dash}</Body>
                            )}
                        </ProfileDataItem>
                    )}

                    {employeeIncentiveCodeEnabled ? (
                        <ProfileDataItem>
                            <LocalizedSmallParagraph variant="secondary" textKey={TextKey.EmployeeIncentiveCode} />
                            {renderListOrDash(
                                user.employeeIncentiveCodes ? compactArray(user.employeeIncentiveCodes) : [],
                                (item) => item
                            )}
                        </ProfileDataItem>
                    ) : null}

                    <ProfileDataItem>
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.Status} />

                        <Stack vertical spacing="100" itemAlignment="start">
                            <UserStatusTag status={user.status} />
                            {user.status === UserStatus.Pending &&
                            user.isVerified === false &&
                            user.enterprises?.length ? (
                                <>
                                    <ResendInviteButton email={user.email} />
                                    <CopyInviteButton userId={user.userId} />
                                </>
                            ) : null}
                        </Stack>
                    </ProfileDataItem>
                </Stack>
            </Stack>

            <Stack vertical spacing="200">
                <Typography textStyle="titleMediumStrong" as="h3">
                    <LocalizedText textKey={TextKey.RolesAndPermissions} />
                </Typography>
                <Stack vertical>
                    <Divider />

                    <ProfileDataItem>
                        <LocalizedSmallParagraph
                            variant="secondary"
                            textKey={activePrivileges.length && showPermissions ? TextKey.Privileges : TextKey.Roles}
                        />

                        <Stack vertical spacing="150">
                            {user.isAdmin ? <LocalizedSmallParagraph textKey={TextKey.IsAdmin} /> : null}

                            {user.roles?.map((item) => (
                                <Body size="small" key={item.id}>
                                    {item.name}
                                </Body>
                            ))}

                            {showPermissions
                                ? activePrivileges.map((privilege) => {
                                      const { label } = localizePrivilege(privilege);
                                      return (
                                          <Body size="small" key={privilege}>
                                              {label}
                                          </Body>
                                      );
                                  })
                                : null}

                            {!user.isAdmin && !user.roles?.length && !activePrivileges.length ? (
                                <Body size="small">{UnicodeEntity.Dash}</Body>
                            ) : null}
                        </Stack>
                    </ProfileDataItem>

                    <ProfileDataItem>
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.ReceiveCustomerMessages} />
                        <LocalizedSmallParagraph
                            textKey={user.options?.receiveCustomerMessages ? TextKey.Yes : TextKey.No}
                        />
                    </ProfileDataItem>

                    <ProfileDataItem>
                        <LocalizedSmallParagraph variant="secondary" textKey={TextKey.ResetTwoFactorShort} />
                        <LocalizedSmallParagraph
                            textKey={user.options?.resetUsersTwoFactorAuthentication ? TextKey.Yes : TextKey.No}
                        />
                    </ProfileDataItem>

                    {userType === UserType.Portfolio ? null : (
                        <ProfileDataItem>
                            <LocalizedSmallParagraph variant="secondary" textKey={TextKey.CashierPlural} />
                            {renderListOrDash(user.cashiers || [], (item) => item.name)}
                        </ProfileDataItem>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

const ProfileDataItem = ({ children }: React.PropsWithChildren<{}>) => (
    <>
        <StackItem padding="200">
            <Grid columnGap="100" templateColumns="minmax(30%, min-content) 1fr">
                {children}
            </Grid>
        </StackItem>
        <Divider />
    </>
);

const SuperiorAvatar = ({ userId }: { userId: string }) => {
    const { data, isFetching } = useEnterpriseProfilesSearch({ searchToken: userId });
    const getAvatarSrc = useGetAvatarSrc();

    const matchedProfile = data?.profiles?.find(
        (profile) => profile.id === userId && profile.data.discriminator === ProfileDataDiscriminator.EnterpriseProfile
    );

    const matchedUser = data?.users?.find(
        (user) =>
            matchedProfile?.data.value &&
            'userId' in matchedProfile.data.value &&
            user.id === matchedProfile.data.value.userId
    );

    if (isFetching) {
        return <Loader size="small" />;
    }

    if (matchedUser) {
        return (
            <Stack spacing="100" itemAlignment="center">
                <Avatar title={matchedUser.fullName || ''} src={getAvatarSrc(matchedUser.imageId)} size="small" />
                <Body size="small">{matchedUser.fullName}</Body>
            </Stack>
        );
    }

    return <Body size="small">{UnicodeEntity.Dash}</Body>;
};

const renderListOrDash = <T,>(data: Array<T> | null | undefined, printFn: (item: T) => string) =>
    data?.length ? (
        <Stack vertical spacing="150">
            {data.map((item) => (
                <Body size="small" key={printFn(item)}>
                    {printFn(item)}
                </Body>
            ))}
        </Stack>
    ) : (
        <Body size="small">{UnicodeEntity.Dash}</Body>
    );
